// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { DI_TOKENS, resolve } from '@src/di/container';

import Modal from '@src/components/common/Modal';
import StatusMessageModal from '@src/components/common/StatusMessageModal';
import { UploadCredentialError } from '@src/services/upload';
import {
  useDatoClient,
  useDatoPerson,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';

import { StepOne, StepThree, StepTwo } from './steps';
import { useFolderPicker } from './useFolderPicker';
import { Step, useStoryCreator } from './useStoryCreator';

import type { ClassifiedUploadError } from '@src/services/upload/UploadErrorClassifier';
import type { Story } from '@src/types.ts/story';

const AddStoriesModal = observer(
  ({
    closeModal,
    onStoryBlankCreated,
    onStoryInitialized,
    onStoryCreated,
    onStoryProgress,
    onSave,
    modalId,
  }: {
    closeModal: (modalId: string) => void;
    onStoryBlankCreated: (storyId: string, modalId: string) => void;
    onStoryInitialized: (s: Story, modalId: string) => void;
    onStoryCreated: (s: Story, modalId: string) => void;
    onStoryProgress: (percent: number, storyId?: string) => void;
    onSave?: () => void;
    modalId: string;
  }) => {
    const videoCreator = useVideoCreatorStore();
    const userIdentity = useUserIdentity();
    const datoClientStore = useDatoClient();
    const datoPerson = useDatoPerson();
    const [isStatusMessageModalOpen, setIsStatusMessageModalOpen] =
      useState<boolean>(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [lastClassifiedError, setLastClassifiedError] =
      useState<ClassifiedUploadError | null>(null);
    const { organization } = videoCreator;

    const {
      showFolderPicker,
      folderOptions,
      selectedFolderId,
      setSelectedFolderId,
      assignStoryToFolder,
    } = useFolderPicker(organization?.id);

    const storyCreatorParams = useMemo(
      () => ({
        videoCreator,
        organization: videoCreator.organization,
        datoClientStore,
        userIdentity,
        datoPersonId: datoPerson?.person?.id,
        modalId,
        onStoryBlankCreated: (storyId: string) =>
          onStoryBlankCreated(storyId, modalId),
        onStoryInitialized: (s: Story) => {
          onStoryInitialized(s, modalId);
          assignStoryToFolder(s.id);
        },
        onStoryCreated: (s: Story) => onStoryCreated(s, modalId),
        onStoryProgress: onStoryProgress,
        onError: (error: unknown, classified?: ClassifiedUploadError) => {
          setLastClassifiedError(classified ?? null);
          setStatusMessage(
            classified?.userMessage ??
              (error instanceof UploadCredentialError
                ? error.userMessage
                : null),
          );
          setIsStatusMessageModalOpen(true);
        },
        storyCreationService: resolve(DI_TOKENS.STORY_CREATION_SERVICE),
      }),
      [
        videoCreator,
        videoCreator.organization,
        datoClientStore,
        userIdentity,
        datoPerson?.person?.id,
        onStoryBlankCreated,
        onStoryInitialized,
        onStoryCreated,
        modalId,
        onStoryProgress,
        organization,
        assignStoryToFolder,
      ],
    );

    const {
      step,
      goToNextStep,
      canGoToStep,
      handleStoryVideoUpload,
      handleStoryUrlUpload,
      retryUpload,
      uploadMechanism,
      storyName,
      setStoryName,
      storyLanguage,
      setStoryLanguage,
      storyType,
      setStoryType,
      videoMusicStrategy,
      setVideoMusicStrategy,
      audioEnhancementDisabled,
      storyArtifactsFiles,
      storyArtifactsVideoFiles,
      maxUploadSizeGB,
      maxStoryTitleLength,
      setZoomRecordingData,
    } = useStoryCreator(storyCreatorParams);

    const prevArtifactsCountRef = useRef<number>(0);
    const prevArtifactsVideoCountRef = useRef<number>(0);

    // Navigate to Story Manager (or other destination) when save is triggered
    useEffect(() => {
      if (step === Step.five && onSave) {
        // Defer to avoid triggering navigation during render cycle
        const id = setTimeout(onSave, 0);
        return () => clearTimeout(id);
      }
    }, [step, onSave]);

    // Instrument artifact additions (doc + video) by detecting count increases
    useEffect(() => {
      prevArtifactsCountRef.current = storyArtifactsFiles.length;
    }, [storyArtifactsFiles, videoCreator, uploadMechanism]);

    useEffect(() => {
      prevArtifactsVideoCountRef.current = storyArtifactsVideoFiles.length;
    }, [storyArtifactsVideoFiles, videoCreator, uploadMechanism]);

    const renderStep = () => {
      switch (step) {
        case Step.one:
          return (
            <StepOne
              goToNextStep={goToNextStep}
              canGoToStep={canGoToStep(Step.two)}
              handleStoryVideoUpload={(files: File[]) => {
                if (files.length === 0) {
                  return;
                }
                if (files[0].size > maxUploadSizeGB * 1000 * 1000 * 1000) {
                  console.warn(
                    `File size exceeds ${maxUploadSizeGB} GB, size:`,
                    files[0].size,
                  );
                  setStatusMessage(
                    `File size exceeds the maximum upload size of ${maxUploadSizeGB} GB`,
                  );
                  setIsStatusMessageModalOpen(true);
                  return;
                }
                handleStoryVideoUpload(files);
              }}
              handleStoryUrlUpload={handleStoryUrlUpload}
              setStoryName={setStoryName}
              setZoomRecordingData={setZoomRecordingData}
              maxUploadSizeGB={maxUploadSizeGB}
            />
          );
        case Step.two:
          return (
            <StepTwo
              goToNextStep={goToNextStep}
              canGoToStep={canGoToStep(Step.three)}
              storyType={storyType}
              setStoryType={setStoryType}
              videoMusicStrategy={videoMusicStrategy}
              setVideoMusicStrategy={setVideoMusicStrategy}
              audioEnhancementDisabled={audioEnhancementDisabled}
            />
          );
        case Step.three:
          return (
            <StepThree
              goToNextStep={goToNextStep}
              canGoToStep={canGoToStep(Step.five)}
              storyName={storyName}
              setStoryName={setStoryName}
              storyLanguage={storyLanguage}
              setStoryLanguage={setStoryLanguage}
              allowLangSelect={
                videoCreator.organization?.allowLangSelectAtUserStoryUpload
              }
              maxStoryTitleLength={maxStoryTitleLength}
              showFolderPicker={showFolderPicker}
              folderOptions={folderOptions}
              selectedFolderId={selectedFolderId}
              setSelectedFolderId={setSelectedFolderId}
            />
          );
        default:
          return null;
      }
    };

    const handleCloseModal = () => {
      // The upload kicks off in Step One and keeps running in the background
      // after the modal closes. Fire the save/navigation callback so closing
      // the modal early (e.g. skipping the folder step) still lands the user in
      // the Story Manager to watch progress — the store defers the save until
      // the story id exists when the close races blank-story creation. Being
      // past Step One is what marks an upload as initiated (only the upload
      // handlers advance the wizard); dismissing at Step One stays put.
      if (step !== Step.one) {
        onSave?.();
      }
      closeModal(modalId);
    };

    if (isStatusMessageModalOpen) {
      return (
        <StatusMessageModal
          enabled
          variant="user-upload-failed"
          customMessage={statusMessage}
          onClose={handleCloseModal}
          retryable={lastClassifiedError?.retryable}
          onRetry={
            lastClassifiedError?.retryable
              ? () => {
                  setIsStatusMessageModalOpen(false);
                  setLastClassifiedError(null);
                  retryUpload();
                }
              : undefined
          }
        />
      );
    }

    if (step === Step.five) {
      return null;
    }

    return (
      <Modal isOpen closeModal={handleCloseModal}>
        <Wrapper data-testid="add-stories-modal">{renderStep()}</Wrapper>
      </Modal>
    );
  },
);

const Wrapper = styled.div`
  width: 440px;
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 48px 24px;
  box-sizing: content-box;
  background: var(--background);
`;

export default AddStoriesModal;
